import React, { useEffect } from 'react'

/**
 * Swaps Zentra + WeGuard product-wordmark `src` based on the current
 * `<html data-theme>` value.
 *
 *  - light theme: `/assets/logo-{zentra|weguard}.png`        (black text)
 *  - dark  theme: `/assets/logo-{zentra|weguard}-dark.png`   (white text)
 *
 * The site already uses the same light/dark image-pair pattern for
 * its own Pentabay wordmark; this gives the two affected product
 * wordmarks parity without modifying the source HTML.
 */

const SWAPS = [
  { match: 'logo-zentra',   light: '/assets/logo-zentra.png',   dark: '/assets/logo-zentra-dark.png'   },
  { match: 'logo-weguard',  light: '/assets/logo-weguard.png',  dark: '/assets/logo-weguard-dark.png'  },
  { match: 'logo-ava',      light: '/assets/logo-ava.png',      dark: '/assets/logo-ava-dark.png'      },
  { match: 'logo-neo',      light: '/assets/logo-neo.png',      dark: '/assets/logo-neo-dark.png'      },
  { match: 'logo-mydart',   light: '/assets/logo-mydart.png',   dark: '/assets/logo-mydart-dark.png'   },
  { match: 'logo-trackme',  light: '/assets/logo-trackme.png',  dark: '/assets/logo-trackme-dark.png'  },
]

function isDarkTheme() {
  return document.documentElement.getAttribute('data-theme') === 'dark'
}

function applyTheme() {
  const dark = isDarkTheme()
  const imgs = document.querySelectorAll('img')
  for (const img of imgs) {
    const src = img.getAttribute('src') || ''
    for (const { match, light, dark: darkSrc } of SWAPS) {
      if (!src.includes(match)) continue
      const want = dark ? darkSrc : light
      // Only write when the resolved URL doesn't already point at the
      // desired variant - avoids gratuitous load events.
      if (!img.src.endsWith(want)) img.setAttribute('src', want)
      break
    }
  }
}

export default function LogoThemeSwap() {
  useEffect(() => {
    applyTheme()

    // Theme attribute changes on <html>
    const themeMo = new MutationObserver(applyTheme)
    themeMo.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    })

    // New images appearing (SPA route mounts product cards)
    const bodyMo = new MutationObserver((muts) => {
      for (const m of muts) {
        for (const n of m.addedNodes) {
          if (n.nodeType !== 1) continue
          if (n.tagName === 'IMG' || (n.querySelector && n.querySelector('img'))) {
            applyTheme()
            return
          }
        }
      }
    })
    bodyMo.observe(document.body, { childList: true, subtree: true })

    return () => {
      themeMo.disconnect()
      bodyMo.disconnect()
    }
  }, [])

  return null
}
